import { DetailsPageIsland } from '@rocco/ui/floating-island/components/detailspage-island/DetailsPageIsland';

import { ReactComponent as EnquiryIcon } from '@rocco/icons/svg/message.svg';
import { BookmarkIcon } from 'lucide-react';
import { useRootData } from '~/modules/root';
import { Button } from '@rocco/components/button';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useTracker } from '@archipro-website/tracker';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { cn } from '@rocco/utils/cn';

import { type DetailsPageIslandImageProps } from '@rocco/ui/floating-island/components/detailspage-island/DetailsPageIslandImage';

interface ProductIslandProps {
    product: Pick<
        ProductPageLoaderData['product'],
        'ID' | 'Title' | 'DefaultImage'
    >;
    professional: Pick<
        ProductPageLoaderData['product']['Professional'],
        'Title' | 'Link' | 'CompanyLogo' | 'LogoBackgroundColor'
    >;
    openEnquiryModal: (changeUrl?: boolean, customHeading?: string) => void;
    showProductImage?: boolean;
}

export const ProductIsland = ({
    product,
    professional,
    openEnquiryModal,
    showProductImage = false,
}: ProductIslandProps) => {
    const { Title: productTitle, ID: productId } = product;

    const {
        Title: professionalTitle,
        Link: professionalLink,
        CompanyLogo,
        LogoBackgroundColor,
    } = professional;

    const { appDisplayMode } = useRootData();

    const tracker = useTracker();

    const onEnquiryClick = (label: string) => {
        trackEnquireReveal(tracker, {
            professionalName: professionalTitle,
            itemID: productId,
            itemType: 'product',
            source: 'ProductIsland',
            ctaLabel: label,
        });

        openEnquiryModal(false, label);
    };

    const image: DetailsPageIslandImageProps = showProductImage
        ? {
              image: {
                  src:
                      product.DefaultImage?.LibraryImageFile?.DetailPageSquare
                          ?.Desktop ?? '',
                  alt: product.Title,
                  multiplied: product.DefaultImage?.IsMultiplied,
              },
              size: 'full',
          }
        : {
              image: {
                  src: CompanyLogo,
                  alt: professionalTitle,
              },
              background: LogoBackgroundColor,
              link: professionalLink,
              size: 'professionalLogo',
          };

    return (
        <DetailsPageIsland
            key={`product-island-${productId}`}
            isClickable={false}
            scrollThreshold={0}
            image={image}
            displayMode={appDisplayMode}
            title={productTitle}
            description={professionalTitle}
            primaryAction={
                <ProductIslandEnquiryButton
                    label={'Request Price / Quote'}
                    onClick={() => onEnquiryClick('Request Price / Quote')}
                    displayMode={appDisplayMode}
                    icon={
                        <EnquiryIcon
                            aria-label="Request Price / Quote"
                            className="w-4 fill-black"
                        />
                    }
                    type="primary"
                />
            }
            secondaryAction={
                <ProductIslandAddToDesignBoardButton
                    label="Shortlist Product"
                    displayMode={appDisplayMode}
                    productId={productId}
                />
            }
        />
    );
};

const ProductIslandEnquiryButton = ({
    label,
    onClick,
    displayMode,
    icon,
    type,
}: {
    label: string;
    onClick: () => void;
    displayMode: DisplayMode;
    icon: React.ReactNode;
    type: 'primary' | 'secondary';
}) => {
    const compact = displayMode === DisplayMode.Mobile;

    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color={type === 'primary' ? 'primary' : 'secondary70'}
            onClick={onClick}
        >
            <span className="flex items-center gap-2">
                {compact ? null : label}
                {icon}
            </span>
        </Button>
    );
};

const ProductIslandAddToDesignBoardButton = ({
    label,
    displayMode,
    productId,
}: {
    label: string;
    displayMode: DisplayMode;
    productId: number;
}) => {
    const compact = displayMode === DisplayMode.Mobile;
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const saved = isInDesignBoard(productId, 'ProductPin');
    return (
        <Button
            type="button"
            size={compact ? 'square42' : 'default'}
            color="secondary70"
            onClick={() => handleFollow({ id: productId }, 'ProductPin')}
        >
            <span className="flex items-center gap-2">
                {compact ? null : saved ? 'Shortlisted' : 'Shortlist'}
                <BookmarkIcon
                    aria-label={label}
                    className={cn(
                        'w-4',
                        saved ? 'fill-gray-900' : 'stroke-gray-900'
                    )}
                />
            </span>
        </Button>
    );
};
